/**
 * MobX ViewModel для шага «Счета СФУ» (смена формы управления).
 *
 * Атомарный flow (2026-05-04): вместо двухшагового
 *   POST /api/RegistrationAccounts → POST /api/RegistrationAccounts/{id}/doc[/reuse]
 * один запрос:
 *   POST /api/Registrations/{registrationId}/sfu-accounts
 * Бэк создаёт RegistrationAccount + Scan + RegistrationDoc в одной транзакции
 * (RequestsSfuAccountRequest). AccountTypeCode определяется группой услуг и
 * приходит с фронта только информативно (read-only в UI).
 *
 * Скан до момента submit'а хранится в RowState (scanExtension + scanBase64Data).
 * Когда строка готова (IBAN валиден по checkIban, BIC=8, плюс либо скан в state, либо
 * sourceRegistrationAccountId выбран) — RowEditor триггерит submitRow,
 * который отправляет один атомарный запрос.
 *
 * Удаление сохранённой строки — DELETE /api/RegistrationAccounts/{id};
 * cascade на бэке очистит RegistrationDoc и Scan.
 */
import { injectable } from 'inversify';
import { makeAutoObservable, runInAction } from 'mobx';
import { notifications } from '@shared/utils/notifications';
import { getFileErrorMessage } from '@shared/utils/files';
import { checkIban } from '@shared/utils/helpers/validation/validateIban';
import {
  getApiRegistrationsIdGroupsWithCurrentAccounts,
  getGetApiRegistrationsIdGroupsWithCurrentAccountsQueryKey,
  postApiRegistrationsRegistrationIdSfuAccounts
} from '@/shared/api/orval/registrations/registrations';
import { deleteApiRegistrationAccountsId } from '@/shared/api/orval/registration-accounts/registration-accounts';
import { ResponsesGroupWithAccountResponse } from '@/shared/api/orval/models/responsesGroupWithAccountResponse';
import { RequestsSfuAccountRequest } from '@/shared/api/orval/models/requestsSfuAccountRequest';
import { queryClient } from '@/shared/api/reactQuery';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Тип счёта группы услуг для шага СФУ.
 * EXTERNAL для этого шага не применим — на бэке проверяется через CheckCanAdd.
 */
export type RowAccountTypeCode = 'CURRENT' | 'SAVINGS';

/**
 * Состояние одной строки таблицы (одна группа услуг).
 * Хранится в ViewModel как observable map.
 *
 * До успешного submit'а:
 *   scanExtension/scanBase64Data — буфер файла, отправляется в составе атомарного запроса
 *   sourceRegistrationAccountId  — установлен при выборе reuse (тогда буфер скана не нужен)
 *
 * После успешного submit'а:
 *   registrationAccountId, scanDocId — заполнены; UI блокирует поля.
 */
export interface RowState {
  serviceGroupId: number;
  serviceGroupCode: string;
  serviceGroupNameRu: string;
  serviceGroupNameKz: string;
  /** Тип счёта группы — read-only, определяется группой (BIG_REPAIR* → SAVINGS, иначе CURRENT) */
  serviceGroupAccountTypeCode: RowAccountTypeCode;
  /** IBAN счёта (заполняется пользователем или копируется из source при reuse) */
  iban: string;
  /** BIC банка (автоподстановка через каталог банков по slice(4,7) IBAN) */
  bic: string;
  /** Имя выбранного файла скана — для UI («файл выбран, ещё не отправлен») */
  scanFileName?: string;
  /** Расширение файла — буфер для атомарного submit'а */
  scanExtension?: string;
  /** base64 содержимого файла — буфер для атомарного submit'а */
  scanBase64Data?: string;
  /** registrationAccountId строки-источника при reuse (взаимоисключим со scanExtension/scanBase64Data) */
  sourceRegistrationAccountId?: number;
  /** ID счёта заявки (RegistrationAccount.Id), создаётся атомарным submit'ом */
  registrationAccountId?: number;
  /** ID скан-документа (RegistrationDoc.Id), создаётся атомарным submit'ом */
  scanDocId?: number;
  /** Идёт ли submit (spinner для строки) */
  isSaving: boolean;
  /**
   * Последний submit упал — useEffect-watch не должен повторять автосабмит,
   * чтобы не зациклиться на 401/403/500. Сбрасывается на любую мутацию полей
   * (пользователь поправил IBAN/BIC/файл — значит хочет попробовать ещё раз).
   */
  submitFailed?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Определяет тип счёта группы услуг.
 *  - Если бэк уже знает текущий тип счёта группы (`currentAccountTypeCode`) — берём его.
 *  - Иначе по коду группы: BIG_REPAIR* → SAVINGS (накопительный), остальные → CURRENT.
 * Это согласуется с серверной логикой CheckCanAdd для шага СФУ.
 */
const resolveAccountTypeCode = (g: ResponsesGroupWithAccountResponse): RowAccountTypeCode => {
  const fromBackend = g.currentAccountTypeCode;
  if (fromBackend === 'SAVINGS' || fromBackend === 'CURRENT') return fromBackend;
  const code = g.serviceGroupCode ?? '';
  return code.startsWith('BIG_REPAIR') ? 'SAVINGS' : 'CURRENT';
};

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class ChangeUnionTypeAccountsViewModel {
  /** Список групп с состоянием строк (serviceGroupId → RowState) */
  rows: Map<number, RowState> = new Map();

  /** Идёт ли начальная загрузка групп */
  isLoading = false;

  /** ID заявки (устанавливается перед mount) */
  registrationId: number = 0;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── Инициализация ──────────────────────────────────────────────────────────

  /**
   * Загружает список групп услуг с текущими счетами ОСИ.
   * Вызывается при маунте компонента (из useEffect в index.tsx).
   */
  loadGroups = async (registrationId: number): Promise<void> => {
    this.registrationId = registrationId;
    this.isLoading = true;

    try {
      const response = await queryClient.fetchQuery({
        queryKey: getGetApiRegistrationsIdGroupsWithCurrentAccountsQueryKey(registrationId),
        queryFn: ({ signal }) => getApiRegistrationsIdGroupsWithCurrentAccounts(registrationId, signal),
        staleTime: 0
      });

      runInAction(() => {
        const groups: ResponsesGroupWithAccountResponse[] = response?.result ?? [];
        this.rows = new Map(
          groups.map((g) => [
            g.serviceGroupId ?? 0,
            {
              serviceGroupId: g.serviceGroupId ?? 0,
              serviceGroupCode: g.serviceGroupCode ?? '',
              serviceGroupNameRu: g.serviceGroupNameRu ?? '',
              serviceGroupNameKz: g.serviceGroupNameKz ?? '',
              serviceGroupAccountTypeCode: resolveAccountTypeCode(g),
              // Если в response уже есть сохранённый RegistrationAccount этой заявки —
              // подтягиваем IBAN/BIC/ID-шники, чтобы UI отрендерил строку как «уже сохранённую»
              // (поля disabled через isLocked, chip «Скан загружен» через isLocked && scanDocId).
              // scanFileName/Extension/Base64Data НЕ заполняем — файл уже на бэке, повторно качать не нужно.
              iban: g.registrationAccount ?? '',
              bic: g.registrationBankBic ?? '',
              scanFileName: undefined,
              scanExtension: undefined,
              scanBase64Data: undefined,
              sourceRegistrationAccountId: undefined,
              registrationAccountId: g.registrationAccountId ?? undefined,
              scanDocId: g.registrationDocId ?? undefined,
              isSaving: false,
              submitFailed: false
            }
          ])
        );
      });
    } catch {
      notifications.error();
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // ─── Геттеры ────────────────────────────────────────────────────────────────

  /** Строки таблицы в порядке вставки */
  get rowList(): RowState[] {
    return Array.from(this.rows.values());
  }

  /**
   * Список строк, успешно сохранённых на бэке —
   * могут быть использованы как source для reuse в других группах.
   */
  get completedRows(): RowState[] {
    return this.rowList.filter((r) => r.registrationAccountId && r.scanDocId);
  }

  /**
   * true — все строки готовы (есть registrationAccountId + scanDocId).
   * Используется для активации кнопки «Далее».
   */
  get isAllRowsComplete(): boolean {
    if (this.rows.size === 0) return false;
    return this.rowList.every((r) => r.registrationAccountId != null && r.scanDocId != null);
  }

  // ─── Мутации полей строки (до submit'а) ─────────────────────────────────────

  /**
   * Обновить IBAN. Ввод вручную сбрасывает ранее выбранный reuse-source —
   * пользователь начал свой ввод, значит хочет создать новый счёт со скан-файлом.
   */
  setRowIban = (serviceGroupId: number, iban: string): void => {
    const row = this.rows.get(serviceGroupId);
    if (!row || row.registrationAccountId) return;
    this.rows.set(serviceGroupId, {
      ...row,
      iban,
      sourceRegistrationAccountId: undefined,
      submitFailed: false
    });
  };

  /** Обновить BIC. */
  setRowBic = (serviceGroupId: number, bic: string): void => {
    const row = this.rows.get(serviceGroupId);
    if (!row || row.registrationAccountId) return;
    this.rows.set(serviceGroupId, { ...row, bic, submitFailed: false });
  };

  /**
   * Положить выбранный файл скана в state.
   * Сбрасывает sourceRegistrationAccountId — наличие файла означает «новый счёт со своим сканом»,
   * не reuse. Если потребуется reuse — пользователь выберет опцию в Autocomplete и буфер очистится.
   */
  setRowScan = (
    serviceGroupId: number,
    fileName: string,
    extension: string,
    base64Data: string
  ): void => {
    const row = this.rows.get(serviceGroupId);
    if (!row || row.registrationAccountId) return;
    this.rows.set(serviceGroupId, {
      ...row,
      scanFileName: fileName,
      scanExtension: extension,
      scanBase64Data: base64Data,
      sourceRegistrationAccountId: undefined,
      submitFailed: false
    });
  };

  /** Сбросить выбранный (но ещё не отправленный) скан-файл. */
  clearRowScan = (serviceGroupId: number): void => {
    const row = this.rows.get(serviceGroupId);
    if (!row || row.registrationAccountId) return;
    this.rows.set(serviceGroupId, {
      ...row,
      scanFileName: undefined,
      scanExtension: undefined,
      scanBase64Data: undefined
    });
  };

  /**
   * Установить reuse-источник (выбор существующего IBAN из Autocomplete).
   * Наследует IBAN/BIC от source и очищает буфер скана — для reuse файл не нужен.
   */
  setRowReuseSource = (serviceGroupId: number, sourceRegistrationAccountId: number): void => {
    const row = this.rows.get(serviceGroupId);
    const sourceRow = this.rowList.find((r) => r.registrationAccountId === sourceRegistrationAccountId);
    if (!row || !sourceRow || row.registrationAccountId) return;

    this.rows.set(serviceGroupId, {
      ...row,
      iban: sourceRow.iban,
      bic: sourceRow.bic,
      scanFileName: undefined,
      scanExtension: undefined,
      scanBase64Data: undefined,
      sourceRegistrationAccountId,
      submitFailed: false
    });
  };

  // ─── Атомарный submit строки ────────────────────────────────────────────────

  /**
   * Отправляет один атомарный запрос на создание счёта+скана для группы.
   * Безопасно повторяемый: при наличии registrationAccountId — no-op (защита от
   * двойных вызовов из useEffect-watch'а в RowEditor).
   *
   * Готовность строки:
   *   checkIban(IBAN) && BIC.length === 8 && (есть скан в state ИЛИ выбран reuse-source)
   */
  submitRow = async (serviceGroupId: number): Promise<void> => {
    const row = this.rows.get(serviceGroupId);
    if (!row) return;
    if (row.registrationAccountId) return; // уже сохранён
    if (row.isSaving) return; // защита от гонки на быстрых useEffect

    if (row.submitFailed) return; // прошлый submit упал — ждём правки полей пользователем
    // OSI-806: строка не уходит на бэк, пока IBAN не пройдёт формат + контрольную сумму.
    // Раньше проверялась только длина 20, поэтому мусорный счёт автосабмитился и
    // возвращался общим тостом «Указан неверный счет».
    if (!checkIban(row.iban.trim())) return;
    if (row.bic.trim().length !== 8) return;

    const hasScan = !!(row.scanExtension && row.scanBase64Data);
    const hasReuse = !!row.sourceRegistrationAccountId;
    if (!hasScan && !hasReuse) return;

    this.rows.set(serviceGroupId, { ...row, isSaving: true });

    try {
      const body: RequestsSfuAccountRequest = hasReuse
        ? {
            serviceGroupId,
            account: row.iban.trim(),
            bankBic: row.bic.trim(),
            sourceRegistrationAccountId: row.sourceRegistrationAccountId
          }
        : {
            serviceGroupId,
            account: row.iban.trim(),
            bankBic: row.bic.trim(),
            scanExtension: row.scanExtension,
            scanBase64Data: row.scanBase64Data
          };

      const response = await postApiRegistrationsRegistrationIdSfuAccounts(this.registrationId, body);
      const result = response?.result;

      if (!result?.registrationAccountId) {
        // Бэк вернул ApiResponse без result (например, ошибка валидации) — снимаем спиннер.
        notifications.error();
        runInAction(() => {
          const updated = this.rows.get(serviceGroupId);
          if (updated) this.rows.set(serviceGroupId, { ...updated, isSaving: false, submitFailed: true });
        });
        return;
      }

      runInAction(() => {
        const updated = this.rows.get(serviceGroupId);
        if (!updated) return;
        this.rows.set(serviceGroupId, {
          ...updated,
          registrationAccountId: result.registrationAccountId,
          scanDocId: result.registrationDocId,
          // Буфер файла больше не нужен — он сохранён в Scan на бэке.
          scanExtension: undefined,
          scanBase64Data: undefined,
          isSaving: false
        });
      });
    } catch (e) {
      // В этом POST уходит base64 скана — значит возможен 413 от внешнего nginx.
      // getFileErrorMessage разворачивает i18n-ключ из интерцептора вместо пустой «Ошибки».
      notifications.toast(getFileErrorMessage(e), 'error');
      runInAction(() => {
        const updated = this.rows.get(serviceGroupId);
        if (updated) this.rows.set(serviceGroupId, { ...updated, isSaving: false, submitFailed: true });
      });
    }
  };

  // ─── Удаление сохранённой строки ────────────────────────────────────────────

  /**
   * Удаляет уже сохранённый RegistrationAccount (cascade на бэке очистит RegistrationDoc/Scan).
   * Очищает все «после-submit'а» поля, оставляя серую строку готовой к повторному заполнению.
   */
  removeRow = async (serviceGroupId: number): Promise<void> => {
    const row = this.rows.get(serviceGroupId);
    if (!row?.registrationAccountId) return;

    this.rows.set(serviceGroupId, { ...row, isSaving: true });

    try {
      await deleteApiRegistrationAccountsId(row.registrationAccountId);
      runInAction(() => {
        const updated = this.rows.get(serviceGroupId);
        if (!updated) return;
        this.rows.set(serviceGroupId, {
          ...updated,
          iban: '',
          bic: '',
          scanFileName: undefined,
          scanExtension: undefined,
          scanBase64Data: undefined,
          sourceRegistrationAccountId: undefined,
          registrationAccountId: undefined,
          scanDocId: undefined,
          isSaving: false
        });
      });
    } catch {
      notifications.error();
      runInAction(() => {
        const updated = this.rows.get(serviceGroupId);
        if (updated) this.rows.set(serviceGroupId, { ...updated, isSaving: false });
      });
    }
  };
}

export const ChangeUnionTypeAccountsViewModelToken = Symbol.for('ChangeUnionTypeAccountsViewModel');
